import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ShieldCheck,
  ShieldAlert,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Plus,
  ArrowUpRight,
  Sparkles,
  PackageCheck,
  Layers,
  Box,
  Building2,
  Package,
} from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { EntityLink } from "@/components/shared/EntityLink";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { QCFinalInspectionModal } from "@/components/quality/QCFinalInspectionModal";

interface POQualityTabProps {
  productionOrderId: string;
  order?: any;
  isPlanLocked?: boolean;
}

export function POQualityTab({ productionOrderId, order, isPlanLocked }: POQualityTabProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Search ID can match either uuid id or orderId string (e.g. PO-3081)
  const poCode = order?.orderId || productionOrderId;

  // 1. IPQC Query (Cổng 2)
  const { data: ipqcData, isLoading: ipqcLoading } = useQuery({
    queryKey: ["po-ipqc-list", productionOrderId, poCode],
    queryFn: async () => {
      try {
        const res = await trpcClient.qcProcess.list.query({
          productionOrderId: productionOrderId,
          limit: 50,
        });
        return res;
      } catch {
        return { items: [], total: 0 };
      }
    },
  });

  // 2. FQC Query (Cổng 3)
  const { data: fqcData, isLoading: fqcLoading } = useQuery({
    queryKey: ["po-fqc-list", productionOrderId, poCode],
    queryFn: async () => {
      try {
        const res = await trpcClient.qcFinal.list.query({
          productionOrderId: productionOrderId,
          limit: 200,
        });
        return res;
      } catch {
        return { items: [], total: 0 };
      }
    },
  });

  // 3. NCR Query
  const { data: ncrData, isLoading: ncrLoading } = useQuery({
    queryKey: ["po-ncr-list", productionOrderId, poCode],
    queryFn: async () => {
      try {
        const res = await trpcClient.ncr.list.query({
          search: poCode,
          limit: 50,
        });
        return res;
      } catch {
        return { items: [], total: 0 };
      }
    },
  });

  const ipqcList = ipqcData?.items || [];
  const fqcList = fqcData?.items || [];
  const ncrList = ncrData?.items || [];

  // Quick NCR Dialog state
  const [isNcrModalOpen, setIsNcrModalOpen] = useState(false);
  const [ncrSummary, setNcrSummary] = useState("");
  const [ncrSeverity, setNcrSeverity] = useState<"LOW" | "MEDIUM" | "HIGH" | "CRITICAL">("HIGH");
  const [ncrDefectQty, setNcrDefectQty] = useState<number>(0);
  const [ncrCost, setNcrCost] = useState<number>(0);
  const [isSubmittingNcr, setIsSubmittingNcr] = useState(false);

  // Phase 86: FQC Modal & Industrial Print state
  const [isFqcModalOpen, setIsFqcModalOpen] = useState(false);
  // Status metrics
  const hasFirstPiecePass = ipqcList.some(
    (item: any) => item.checkType === "FIRST_PIECE" && item.status === "PASSED"
  );
  const isFqcPassed = fqcList.some((item: any) => item.status === "PASSED");
  const totalCopq = ncrList.reduce((sum: number, item: any) => sum + (Number(item.costImpact) || 0), 0);

  const handleQuickCreateNCR = async () => {
    if (!ncrSummary.trim()) {
      toast.error("Vui lòng nhập tóm tắt sự cố NCR");
      return;
    }
    try {
      setIsSubmittingNcr(true);
      await trpcClient.ncr.quickCreate.mutate({
        source: "INTERNAL",
        relatedType: "PRODUCTION_ORDER",
        relatedId: poCode,
        issueSummary: `[LSX ${poCode}] ${ncrSummary}`,
        severity: ncrSeverity,
        defectQty: ncrDefectQty || undefined,
        costImpact: ncrCost || undefined,
        responsibleDept: "Xưởng Sản Xuất / KCS",
      });
      toast.success("Đã kích hoạt hồ sơ NCR sự cố thành công!");
      setIsNcrModalOpen(false);
      setNcrSummary("");
      setNcrDefectQty(0);
      setNcrCost(0);
      queryClient.invalidateQueries({ queryKey: ["po-ncr-list", productionOrderId, poCode] });
    } catch (err: any) {
      toast.error(err?.message || "Lỗi khi lập NCR");
    } finally {
      setIsSubmittingNcr(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* ── ACTION TOOLBAR ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-3.5 border border-slate-200 rounded-xl shadow-xs">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-indigo-600" />
          <span className="font-bold text-sm text-slate-900">
            KCS của lệnh
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="h-9 text-xs font-semibold border-slate-300 text-slate-700 hover:bg-slate-50 gap-1.5"
            onClick={() =>
              navigate({
                to: "/qc-process/create" as any,
                search: {
                  productionOrderId: order?.id || productionOrderId,
                  checkType: "FIRST_PIECE",
                } as any,
              })
            }
          >
            <Plus className="h-3.5 w-3.5" />
            Nghiệm thu IPQC
          </Button>

          <Button
            size="sm"
            variant="outline"
            className="h-9 text-xs font-semibold border-emerald-300 text-emerald-800 bg-emerald-50/60 hover:bg-emerald-100/60 gap-1.5"
            onClick={() =>
              navigate({
                to: "/qc-final/create" as any,
                search: {
                  productionOrderId: order?.id || productionOrderId,
                } as any,
              })
            }
          >
            <Sparkles className="h-3.5 w-3.5 text-emerald-600" />
            Nghiệm thu FQC
          </Button>

          <Button
            size="sm"
            className="h-9 text-xs font-bold bg-rose-600 hover:bg-rose-700 text-white gap-1.5 shadow-xs"
            onClick={() => setIsNcrModalOpen(true)}
          >
            <AlertTriangle className="h-3.5 w-3.5" />
            Lập NCR
          </Button>
        </div>
      </div>

      {/* ── Công đoạn LSX & Phím tắt nghiệm thu đầu ca (Rule 8 Link-To-Form) ── */}
      {order?.routingSteps && order.routingSteps.length > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex flex-col gap-2 shadow-2xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-indigo-600" />
              Nghiệm thu đầu ca
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {order.routingSteps.map((step: any) => {
              const isPassed = step.qcStatus === "PASSED";
              return (
                <div
                  key={step.id}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs bg-white ${
                    isPassed
                      ? "border-emerald-200 text-emerald-800"
                      : "border-slate-200 text-slate-700"
                  }`}
                >
                  <span className="font-mono text-slate-400 font-bold">#{step.stepOrder || 1}</span>
                  <span className="font-semibold">{step.name}</span>
                  {isPassed ? (
                    <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px] px-1.5 py-0 font-bold">
                      ✓ Đạt mở máy
                    </Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-[11px] font-bold text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50"
                      onClick={() =>
                        navigate({
                          to: "/qc-process/create" as any,
                          search: {
                            productionOrderId: order.id,
                            routingStepId: step.id,
                            processStep: step.name,
                            machineId: step.machineId,
                            checkType: "FIRST_PIECE",
                          } as any,
                        })
                      }
                    >
                      + Nghiệm thu đầu ca
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── SECTION 1: CỔNG 2 - IPQC KIỂM TRA CÔNG ĐOẠN ── */}
      <Card className="bg-white border-slate-200 shadow-xs">
        <CardHeader className="py-3 px-4 border-b border-slate-100 flex flex-row items-center justify-between bg-slate-50/40">
          <CardTitle className="text-sm font-bold text-slate-800 flex items-center gap-2">
            <Layers className="h-4 w-4 text-indigo-600" />
            Kiểm soát công đoạn
            <Badge variant="outline" className="font-mono text-[10px]">{ipqcList.length}</Badge>
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-indigo-600 hover:text-indigo-800 h-8 gap-1"
            onClick={() => navigate({ to: "/qc-process" as any })}
          >
            Xem tất cả IPQC <ArrowUpRight className="h-3.5 w-3.5" />
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80">
                <TableHead className="w-[140px] text-xs font-bold text-slate-700">Mã Kiểm Tra</TableHead>
                <TableHead className="min-w-[130px] text-xs font-bold text-slate-700">Công Đoạn</TableHead>
                <TableHead className="w-[150px] text-xs font-bold text-slate-700">Phân Loại</TableHead>
                <TableHead className="w-[100px] text-center text-xs font-bold text-slate-700">Mẫu Thử</TableHead>
                <TableHead className="w-[90px] text-center text-xs font-bold text-slate-700">Số Lỗi</TableHead>
                <TableHead className="w-[120px] text-center text-xs font-bold text-slate-700">Kết Quả</TableHead>
                <TableHead className="min-w-[200px] text-xs font-bold text-slate-700">Ghi Chú &amp; Hạng Mục</TableHead>
                <TableHead className="w-[130px] text-xs font-bold text-slate-700">Thời Gian</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ipqcLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-xs text-slate-500">
                    Đang tải dữ liệu kiểm tra công đoạn...
                  </TableCell>
                </TableRow>
              ) : ipqcList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-xs text-slate-400 italic">
                    Chưa có biên bản kiểm tra công đoạn (IPQC) cho lệnh sản xuất này.
                  </TableCell>
                </TableRow>
              ) : (
                ipqcList.map((row: any) => (
                  <TableRow key={row.id} className="hover:bg-slate-50/80">
                    <TableCell>
                      <EntityLink
                        entityType="qcProcess"
                        id={row.id}
                        code={row.processId}
                        variant="badge"
                      />
                    </TableCell>
                    <TableCell className="font-semibold text-xs text-slate-900">
                      {row.processStep}
                    </TableCell>
                    <TableCell>
                      {row.checkType === "FIRST_PIECE" ? (
                        <Badge className="bg-indigo-50 text-indigo-700 border-indigo-200 font-bold text-[10px]">
                          ⭐ ĐẦU CA (FIRST-PIECE)
                        </Badge>
                      ) : row.checkType === "ROUTINE" ? (
                        <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 text-[10px]">
                          ĐỊNH KỲ CA
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
                          TUẦN TRA (PATROL)
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center font-mono font-bold text-xs text-slate-700">
                      {row.qtySample || 5}
                    </TableCell>
                    <TableCell className="text-center font-mono font-bold text-xs">
                      <span className={row.qtyDefect > 0 ? "text-rose-600 font-black" : "text-slate-400"}>
                        {row.qtyDefect || 0}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge
                        className={
                          row.status === "PASSED"
                            ? "bg-emerald-50 text-emerald-800 border-emerald-300 font-bold text-[10px]"
                            : row.status === "WARNING"
                            ? "bg-amber-50 text-amber-800 border-amber-300 font-bold text-[10px]"
                            : "bg-rose-50 text-rose-800 border-rose-300 font-bold text-[10px]"
                        }
                      >
                        {row.status === "PASSED" ? "ĐẠT CHUẨN" : row.status === "WARNING" ? "CẢNH BÁO" : "KHÔNG ĐẠT"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600 max-w-[260px] truncate">
                      {row.notes || "Không có ghi chú"}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 font-mono">
                      {row.checkTime ? new Date(row.checkTime).toLocaleDateString("vi-VN") : "—"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ── LỊCH SỬ ĐÓNG GÓI: 1 bảng duy nhất (phiếu FQC & đóng gói của lệnh), cột theo Nhật ký đóng gói; bấm dòng mở phiếu ── */}
      <Card className="bg-white border-slate-200 shadow-xs" data-testid="po-packaging-log">
        <CardHeader className="py-3 px-4 border-b border-slate-100 flex flex-row items-center justify-between bg-slate-50/40 space-y-0">
          <CardTitle className="text-sm font-bold text-slate-800 flex items-center gap-2">
            <PackageCheck className="h-4 w-4 text-emerald-600" />
            Lịch sử đóng gói
            <Badge variant="outline" className="font-mono text-[10px]">{fqcList.length}</Badge>
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-indigo-600 hover:text-indigo-800 h-8 gap-1"
            data-testid="po-packaging-journal-link"
            onClick={() => navigate({ to: "/factory/packing" as any, search: { q: order?.orderId || poCode } as any })}
          >
            Nhật ký đóng gói <ArrowUpRight className="h-3.5 w-3.5" />
          </Button>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table className="min-w-[980px]">
            <TableHeader>
              <TableRow className="bg-slate-50/80">
                <TableHead className="w-[150px] text-xs font-bold text-slate-700">Mã FQC / Lô</TableHead>
                <TableHead className="min-w-[160px] text-xs font-bold text-slate-700">Thành phẩm</TableHead>
                <TableHead className="w-[90px] text-right text-xs font-bold text-slate-700">SL kiểm</TableHead>
                <TableHead className="w-[90px] text-right text-xs font-bold text-emerald-700">SL đạt</TableHead>
                <TableHead className="w-[80px] text-right text-xs font-bold text-rose-700">SL hư</TableHead>
                <TableHead className="w-[170px] text-xs font-bold text-slate-700">Thùng &amp; Pallet</TableHead>
                <TableHead className="w-[170px] text-xs font-bold text-slate-700">Nhập kho &amp; Giao</TableHead>
                <TableHead className="w-[100px] text-center text-xs font-bold text-slate-700">Kết luận</TableHead>
                <TableHead className="w-[150px] text-right text-xs font-bold text-slate-700">Thao tác</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fqcLoading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-xs text-slate-500">
                    Đang tải lịch sử đóng gói...
                  </TableCell>
                </TableRow>
              ) : fqcList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-xs text-slate-400 italic" data-testid="po-packaging-log-empty">
                    Chưa có phiếu FQC &amp; đóng gói cho lệnh này.
                  </TableCell>
                </TableRow>
              ) : (
                fqcList.map((row: any) => (
                  <TableRow
                    key={row.id}
                    data-testid="po-packaging-log-row"
                    className="hover:bg-slate-50/80 cursor-pointer"
                    onClick={() => navigate({ to: "/qc-final/$checkId" as any, params: { checkId: row.id } as any })}
                  >
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <span className="font-mono font-bold text-indigo-600 text-xs">{row.finalQcId}</span>
                        <span className="font-mono text-[10px] text-slate-500">{row.packingSlip?.lotNumber || "—"}</span>
                        <span className="text-[10px] text-slate-400">
                          {row.checkTime || row.createdAt ? new Date(row.checkTime || row.createdAt).toLocaleDateString("vi-VN") : ""}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="font-semibold text-xs text-slate-900 max-w-[220px]">
                      <span className="line-clamp-2" title={row.productName || order?.productName || ""}>{row.productName || order?.productName || "—"}</span>
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">{Number(row.qtyChecked || 0).toLocaleString("vi-VN")}</TableCell>
                    <TableCell className="text-right font-mono text-xs font-bold text-emerald-700">{Number(row.qtyPassed || 0).toLocaleString("vi-VN")}</TableCell>
                    <TableCell className={`text-right font-mono text-xs ${row.qtyFailed > 0 ? "font-bold text-rose-600" : "text-slate-400"}`}>
                      {Number(row.qtyFailed || 0).toLocaleString("vi-VN")}
                    </TableCell>
                    <TableCell className="text-xs">
                      {row.packingSlip?.status === "PACKED" ? (
                        <div className="space-y-0.5">
                          <div className="font-semibold text-slate-800 flex items-center gap-1">
                            <Box className="w-3.5 h-3.5 text-amber-600" />
                            {Number(row.packingSlip.totalBoxes || 0).toLocaleString("vi-VN")} thùng
                            {row.packingSlip.pcsPerBox ? ` × ${Number(row.packingSlip.pcsPerBox).toLocaleString("vi-VN")}` : ""}
                          </div>
                          <div className="text-[10px] text-slate-500 flex items-center gap-1">
                            <Layers className="w-3 h-3 text-slate-400" />
                            {Number(row.packingSlip.totalPallets || 0).toLocaleString("vi-VN")} pallet · {row.packingSlip.packer?.fullName || "—"}
                          </div>
                        </div>
                      ) : (
                        <span className="text-[10px] text-slate-400 italic">Chưa đóng gói</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      <div className="flex flex-col gap-0.5">
                        {row.packingSlip?.receiptDocNo ? (
                          <span className="flex items-center gap-1 text-[10px] text-emerald-800 font-bold font-mono">
                            <Building2 className="w-3 h-3 text-emerald-600" />
                            {row.packingSlip.receiptDocNo}
                          </span>
                        ) : row.packingSlip ? (
                          <span className="text-[10px] text-amber-700">Chờ đóng gói</span>
                        ) : (
                          <span className="text-[10px] text-slate-400">—</span>
                        )}
                        {row.deliveryInfo?.deliveryCode && (
                          <span className="text-[10px] font-mono text-teal-700">
                            Giao: {row.deliveryInfo.deliveryCode}
                            {row.deliveryInfo.lotDeliveredQty ? ` (${Number(row.deliveryInfo.lotDeliveredQty).toLocaleString("vi-VN")})` : ""}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge
                        className={
                          row.status === "PASSED"
                            ? "bg-emerald-600 text-white font-bold text-[10px]"
                            : row.status === "WARNING"
                            ? "bg-amber-600 text-white font-bold text-[10px]"
                            : row.status === "FAILED"
                            ? "bg-rose-600 text-white font-bold text-[10px]"
                            : "bg-slate-200 text-slate-700 font-bold text-[10px]"
                        }
                      >
                        {row.status === "PASSED" ? "Đạt" : row.status === "WARNING" ? "Nhân nhượng" : row.status === "FAILED" ? "Không đạt" : "Chờ kiểm"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {/* Đóng gói, in tem, nhập kho làm trên Phiếu đóng gói (Sản xuất) */}
                        {row.packingSlip && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-[10px] text-amber-900 hover:bg-amber-50 gap-1 border-amber-300"
                            title="Mở phiếu đóng gói ở Xưởng sản xuất › Đóng gói & Nhập kho"
                            onClick={() => navigate({ to: "/factory/packing" as any, search: { q: row.packingSlip.code } as any })}
                          >
                            <Box className="w-3 h-3" /> {row.packingSlip.code}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
            {fqcList.length > 0 && (
              <tfoot>
                <tr data-testid="po-packaging-log-total" className="border-t border-slate-200 bg-slate-50 font-semibold text-xs text-slate-800">
                  <td className="px-4 py-2" colSpan={2}>Tổng cộng</td>
                  <td className="px-4 py-2 text-right font-mono">{fqcList.reduce((a: number, r: any) => a + Number(r.qtyChecked || 0), 0).toLocaleString("vi-VN")}</td>
                  <td className="px-4 py-2 text-right font-mono text-emerald-700">{fqcList.reduce((a: number, r: any) => a + Number(r.qtyPassed || 0), 0).toLocaleString("vi-VN")}</td>
                  <td className="px-4 py-2 text-right font-mono text-rose-700">{fqcList.reduce((a: number, r: any) => a + Number(r.qtyFailed || 0), 0).toLocaleString("vi-VN")}</td>
                  <td className="px-4 py-2" colSpan={4}>
                    {fqcList.reduce((a: number, r: any) => a + Number(r.packingSlip?.totalBoxes || 0), 0).toLocaleString("vi-VN")} thùng •{" "}
                    {fqcList.reduce((a: number, r: any) => a + Number(r.packingSlip?.totalPallets || 0), 0).toLocaleString("vi-VN")} pallet
                  </td>
                </tr>
              </tfoot>
            )}
          </Table>
        </CardContent>
      </Card>

      {/* ── SECTION 3: HỒ SƠ XỬ LÝ SỰ CỐ NCR & CHI PHÍ COPQ ── */}
      <Card className="bg-white border-slate-200 shadow-xs">
        <CardHeader className="py-3 px-4 border-b border-slate-100 flex flex-row items-center justify-between bg-slate-50/40">
          <CardTitle className="text-sm font-bold text-slate-800 flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-rose-600" />
            Sự cố chất lượng
            <Badge variant="outline" className="font-mono text-[10px]">{ncrList.length}</Badge>
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-rose-600 hover:text-rose-800 h-8 gap-1"
            onClick={() => navigate({ to: "/quality/ncr" as any })}
          >
            Xem tất cả NCR <ArrowUpRight className="h-3.5 w-3.5" />
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80">
                <TableHead className="w-[140px] text-xs font-bold text-slate-700">Mã NCR</TableHead>
                <TableHead className="min-w-[220px] text-xs font-bold text-slate-700">Nội Dung Sự Cố</TableHead>
                <TableHead className="w-[100px] text-center text-xs font-bold text-slate-700">Mức Độ</TableHead>
                <TableHead className="w-[120px] text-center text-xs font-bold text-slate-700">Vòng Đời</TableHead>
                <TableHead className="w-[100px] text-center text-xs font-bold text-slate-700">SL Phế Phẩm</TableHead>
                <TableHead className="w-[120px] text-right text-xs font-bold text-slate-700">Thiệt Hại COPQ</TableHead>
                <TableHead className="min-w-[180px] text-xs font-bold text-slate-700">Bộ Phận Xử Lý</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ncrLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-xs text-slate-500">
                    Đang tải dữ liệu NCR...
                  </TableCell>
                </TableRow>
              ) : ncrList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-xs text-slate-400 italic">
                    Không có sự cố không phù hợp (NCR) nào được ghi nhận cho lệnh này.
                  </TableCell>
                </TableRow>
              ) : (
                ncrList.map((ncr: any) => (
                  <TableRow key={ncr.id} className="hover:bg-slate-50/80">
                    <TableCell>
                      <EntityLink
                        entityType="ncr"
                        id={ncr.id}
                        code={ncr.ncrId || ncr.ncrNumber}
                        variant="badge"
                      />
                    </TableCell>
                    <TableCell className="font-semibold text-xs text-slate-900">
                      {ncr.issueSummary}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge
                        className={
                          ncr.severity === "CRITICAL"
                            ? "bg-rose-600 text-white font-bold text-[10px]"
                            : ncr.severity === "HIGH"
                            ? "bg-amber-600 text-white font-bold text-[10px]"
                            : "bg-slate-100 text-slate-700 border-slate-200 text-[10px]"
                        }
                      >
                        {ncr.severity}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge
                        variant="outline"
                        className={
                          ncr.status === "CLOSED"
                            ? "bg-emerald-50 text-emerald-800 border-emerald-300 font-bold text-[10px]"
                            : ncr.status === "INVESTIGATING"
                            ? "bg-amber-50 text-amber-800 border-amber-300 font-bold text-[10px]"
                            : "bg-rose-50 text-rose-800 border-rose-300 font-bold text-[10px]"
                        }
                      >
                        {ncr.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center font-mono font-bold text-xs text-slate-700">
                      {ncr.defectQty ? `${ncr.defectQty.toLocaleString()} ${ncr.unit || "tờ"}` : "-"}
                    </TableCell>
                    <TableCell className="text-right font-mono font-black text-xs text-rose-700">
                      {ncr.costImpact ? `${Number(ncr.costImpact).toLocaleString()} đ` : "0 đ"}
                    </TableCell>
                    <TableCell className="text-xs text-slate-600">
                      {ncr.responsibleDept || "Nội bộ Xưởng"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ── QUICK CREATE NCR DIALOG ── */}
      <Dialog open={isNcrModalOpen} onOpenChange={setIsNcrModalOpen}>
        <DialogContent className="sm:max-w-[500px] bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-rose-600" />
              Khởi Tạo Phiếu Xử Lý Sự Cố NCR
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Ghi nhận lỗi không phù hợp phát sinh trong quá trình sản xuất lệnh {poCode}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3.5 py-2 text-xs">
            <div className="space-y-1">
              <label className="font-bold text-slate-700">Mô tả tóm tắt sự cố (*):</label>
              <Input
                value={ncrSummary}
                onChange={(e) => setNcrSummary(e.target.value)}
                placeholder="VD: Lệch bài in 2mm, cấn rách nắp gập hộp..."
                className="h-9 text-xs"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="font-bold text-slate-700">Mức độ nghiêm trọng:</label>
                <select
                  value={ncrSeverity}
                  onChange={(e: any) => setNcrSeverity(e.target.value)}
                  className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-xs font-medium"
                >
                  <option value="LOW">LOW - Nhẹ</option>
                  <option value="MEDIUM">MEDIUM - Trung bình</option>
                  <option value="HIGH">HIGH - Nghiêm trọng</option>
                  <option value="CRITICAL">CRITICAL - Rất nghiêm trọng</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="font-bold text-slate-700">Số lượng phế phẩm (tờ):</label>
                <Input
                  type="number"
                  value={ncrDefectQty || ""}
                  onChange={(e) => setNcrDefectQty(Number(e.target.value) || 0)}
                  placeholder="VD: 50"
                  className="h-9 text-xs font-mono"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="font-bold text-slate-700">Ước tính thiệt hại chi phí COPQ (VNĐ):</label>
              <Input
                type="number"
                value={ncrCost || ""}
                onChange={(e) => setNcrCost(Number(e.target.value) || 0)}
                placeholder="VD: 500000"
                className="h-9 text-xs font-mono"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isSubmittingNcr}
              onClick={() => setIsNcrModalOpen(false)}
            >
              Hủy
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={isSubmittingNcr || !ncrSummary.trim()}
              onClick={handleQuickCreateNCR}
              className="bg-rose-600 hover:bg-rose-700 text-white font-bold gap-1.5"
            >
              <CheckCircle2 className="h-4 w-4" />
              {isSubmittingNcr ? "Đang lập..." : "Xác Nhận Kích Hoạt NCR"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 86: Modal Nghiệm Thu FQC & Tự Động Nhập Kho Tại Chỗ */}
      {isFqcModalOpen && (
        <QCFinalInspectionModal
          open={isFqcModalOpen}
          onOpenChange={setIsFqcModalOpen}
          productionOrderId={productionOrderId}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["po-fqc-list"] });
            queryClient.invalidateQueries({ queryKey: ["productionOrder"] });
          }}
        />
      )}


    </div>
  );
}
